import { useGetList, useGetOne } from 'ra-core';
import { CustomerInvoice, GoodsReceipt, PurchaseOrder, VendorBill } from '../../types';

export interface ThreeWayMatchResult {
    loading: boolean;
    applicable: boolean;
    poMatched: boolean;
    grnMatched: boolean;
    amountMatched: boolean;
    fullyMatched: boolean;
}

/** Shared PO / GRN / Value matching logic used by both the list badge and the
 * detail-page badge, so the two views never disagree on the same record. */
export const useThreeWayMatch = (
    record: (CustomerInvoice | VendorBill) | null | undefined,
): ThreeWayMatchResult => {
    const applicable = !!record?.po_id && !!record?.grn_id;

    const { data: po, isPending: isLoadingPO } = useGetOne<PurchaseOrder>(
        'purchase_orders',
        { id: record?.po_id },
        { enabled: applicable },
    );

    const { data: grn, isPending: isLoadingGRN } = useGetOne<GoodsReceipt>(
        'goods_receipts',
        { id: record?.grn_id },
        { enabled: applicable },
    );

    // Reconcile against what was actually RECEIVED (GRN accepted_qty × PO unit
    // price), not just the PO's total — a bill for a partial receipt should
    // match the received value, not the full ordered value. Falls back to
    // the PO total when line-level data isn't linked (older records).
    const { data: grnItems, isPending: isLoadingGrnItems } = useGetList(
        'grn_items',
        { filter: { grn_id: record?.grn_id }, pagination: { page: 1, perPage: 200 } },
        { enabled: applicable },
    );
    const { data: poItems, isPending: isLoadingPoItems } = useGetList(
        'po_items',
        { filter: { po_id: record?.po_id }, pagination: { page: 1, perPage: 200 } },
        { enabled: applicable },
    );

    if (!applicable) {
        return { loading: false, applicable: false, poMatched: false, grnMatched: false, amountMatched: false, fullyMatched: false };
    }

    const loading = isLoadingPO || isLoadingGRN || isLoadingGrnItems || isLoadingPoItems || !po || !grn;
    if (loading) {
        return { loading: true, applicable: true, poMatched: false, grnMatched: false, amountMatched: false, fullyMatched: false };
    }

    const poMatched = !!po;
    const grnMatched = grn!.status !== 'draft' && grn!.status !== 'qa_failed';

    const poItemPriceById = new Map((poItems || []).map((pi: any) => [pi.id, Number(pi.unit_price) || 0]));
    const linkedGrnItems = (grnItems || []).filter((gi: any) => gi.po_item_id != null);
    const receivedValue = linkedGrnItems.reduce(
        (sum: number, gi: any) => sum + (Number(gi.accepted_qty) || 0) * (poItemPriceById.get(gi.po_item_id) ?? 0),
        0,
    );

    const amountMatched = linkedGrnItems.length > 0 && linkedGrnItems.length === (grnItems || []).length
        ? Math.abs((record!.total_amount || 0) - receivedValue) < 1
        : Math.abs((record!.total_amount || 0) - (po!.total_amount || 0)) < 1;

    return {
        loading: false,
        applicable: true,
        poMatched,
        grnMatched,
        amountMatched,
        fullyMatched: poMatched && grnMatched && amountMatched,
    };
};
